import { useSharedFunction, useSharedState } from 'frontlink';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import GridCursor from './GridCursor';

interface GridItem {
	ID: string;
	URL: string;
	Left: number;
	Top: number;
	Width: number;
	Height: number;
	Warp: boolean;
	Flip?: boolean;
	Rotation?: number;
	SenderName?: string;
	/**
	 * 0 if never expires
	 */
	ExpiresMS: number;
}

interface GridState {
	Enabled: boolean;
	SoundEnabled: boolean;
	Width: number;
	Height: number;
	Opacity: number;
	PermanentProtectedAreas?: GridStreamArea[];
	TemporaryProtectedAreas?: GridStreamArea[];
	PremiumAreas?: GridStreamArea[];
	PremiumCellPriceCents: number;
	Items?: GridItem[];
}

interface GridStreamArea {
	ID: string;
	Top: number;
	Left: number;
	Width: number;
	Height: number;
}

const colors = ['#98E2C6', '#BFEDEF', '#9EE37D', '#CFFCFF', '#FFFD82', '#F4BFDB', '#FFE9F3', '#F98948', '#B8B8FF', '#FFD8BE', '#F96F5D', '#FE6847'];

export function Grid(props: { userID: string }) {
	const [remoteGridState] = useSharedState<GridState | null>(`gridOverlay::${props.userID}`, null);
	const [showGridPreview, setShowGridPreview] = useState<boolean>(false);
	useSharedFunction(`gridOverlayPreview::${props.userID}`, displayGrid);
	const [gridState, setGridState] = useState<GridState | null>(null);

	useEffect(() => {
		if (!remoteGridState) return;

		setGridState({
			...remoteGridState,
			Items: remoteGridState.Items?.filter((i) => !i.ExpiresMS || i.ExpiresMS >= new Date().getTime()),
		});

		const interval = setInterval(() => {
			if (!remoteGridState) return;

			setGridState({
				...remoteGridState,
				Items: remoteGridState.Items?.filter((i) => !i.ExpiresMS || i.ExpiresMS >= new Date().getTime()),
			});
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [remoteGridState]);

	// How many boxes on the x- and y-axis
	function displayGrid() {
		if (showGridPreview) {
			// If already showing, ignore
			return;
		}
		setShowGridPreview(true);
		console.log('showGridPreview');
		setTimeout(() => {
			setShowGridPreview(false);
		}, 5000);
	}

	/*
	if (gridState && (!gridState.Items || !gridState.Items.length)) {
		gridState.Items = TestState.Items;
		console.log('USE FAKE DATA');
	}
*/
	// useEffect(() => {
	// 	displayGrid(30, Math.floor(30 * (9 / 16)));
	// }, []);

	if (!gridState || !gridState.Enabled) {
		return null;
	}

	const cellSize = 1920 / gridState.Width;
	const cellWidth = cellSize;
	const cellHeight = cellSize;

	return (
		<div className="absolute top-0 w-screen h-screen">
			<div
				className="w-full h-full relative"
				style={{
					opacity: gridState.Opacity,
				}}
			>
				{gridState.Items?.filter((i) => !!i.URL).map((item) => {
					const style: React.CSSProperties = {
						height: item.Height * cellHeight,
						width: item.Width * cellWidth,
						// initial top and left, for animation
						top: 0 - item.Height * cellHeight,
						left: 0 - item.Width * cellWidth,
						position: 'absolute',
						backgroundColor: showGridPreview ? 'rgba(100,100,100,0.5)' : undefined,
					};
					const color = colors[(item.SenderName ?? '0').charCodeAt(0) % (colors.length - 1)];
					return (
						<div key={item.ID}>
							<motion.div
								key={item.ID}
								animate={{
									top: item.Top * cellHeight,
									left: item.Left * cellWidth,
								}}
								transition={{
									duration: 1,
									ease: 'circInOut',
								}}
								style={style}
							>
								{gridState.SoundEnabled && <audio src="/click.mp3" autoPlay />}
								<div style={createItemStyle(item)} className="w-full h-full bg-no-repeat bg-center" />
							</motion.div>
							<motion.div
								key={item.ID + '_cursor'}
								animate={{
									top: [
										0 - item.Height * cellHeight,
										item.Top * cellHeight + (item.Height * cellHeight) / 2,
										item.Top * cellHeight + (item.Height * cellHeight) / 2,
										0 - item.Height * cellHeight,
									],
									left: [
										0 - item.Width * cellWidth,
										item.Left * cellWidth + (item.Width * cellWidth) / 2,
										item.Left * cellWidth + (item.Width * cellWidth) / 2,
										0 - item.Width * cellWidth,
									],
								}}
								transition={{
									duration: 3,
									ease: 'circInOut',
								}}
								className="absolute w-full h-full flex items-center justify-center"
								style={{
									position: 'absolute',
									width: 'auto',
									height: 'auto',
								}}
							>
								<GridCursor fill={color} />
								{item.SenderName && (
									<p
										className="px-1 border-2 border-black rounded-md mt-[-4px] ml-[-8px] text-black"
										style={{
											backgroundColor: color,
										}}
									>
										{item.SenderName}
									</p>
								)}
							</motion.div>
						</div>
					);
				})}
				{showGridPreview && (
					<>
						{new Array(gridState.Width * gridState.Height).fill(null).map((_, idx) => {
							const x = idx % gridState.Width;
							const y = Math.floor(idx / gridState.Width);
							return (
								<div
									key={idx}
									className="absolute border-[2px] border-orange-500"
									style={{
										left: x * cellWidth,
										top: y * cellHeight,
										width: cellWidth,
										height: cellHeight,
									}}
								/>
							);
						})}

						{gridState.PremiumAreas?.map((item) => {
							const style: React.CSSProperties = {
								height: item.Height * cellHeight,
								width: item.Width * cellWidth,
								top: item.Top * cellHeight,
								left: item.Left * cellWidth,
								position: 'absolute',
							};
							return (
								<div
									key={item.ID}
									style={{
										...style,
										background: `rgba(255, 0, 255, 0.5)`,
										backgroundSize: 'contain',
										backgroundRepeat: 'no-repeat',
										backgroundPosition: 'center',
									}}
								/>
							);
						})}
						{gridState.PermanentProtectedAreas?.map((item) => {
							const style: React.CSSProperties = {
								height: item.Height * cellHeight,
								width: item.Width * cellWidth,
								top: item.Top * cellHeight,
								left: item.Left * cellWidth,
								position: 'absolute',
							};
							return (
								<div
									key={item.ID}
									style={{
										...style,
										background: `rgba(255, 0, 0, 0.5)`,
										backgroundSize: 'contain',
										backgroundRepeat: 'no-repeat',
										backgroundPosition: 'center',
									}}
								/>
							);
						})}
					</>
				)}
			</div>
		</div>
	);
}

function createItemStyle(item: GridItem) {
	let transform = item.Flip ? 'scaleX(-1)' : undefined;
	switch (item.Rotation) {
		case 180:
			transform = `rotate(180deg) ${transform ?? ''}`;
			break;
		case 90:
		case 270:
			// after rotate, width and height must be swapped, so the cell still occupies the same space
			transform = `rotate(${item.Rotation}deg) scaleX(${item.Height / item.Width}) scaleY(${item.Width / item.Height}) ${transform ?? ''}`;
			break;
	}
	return {
		backgroundImage: `url('${item.URL}')`,
		backgroundSize: item.Warp ? '100% 100%' : 'contain',
		transform: transform,
	};
}
